import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import type { Condition } from 'types/notification';
import * as notificationApi from 'api/notificationApi';

import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/notificationReducer/registrationSlice';
import { listActions } from 'store/reducers/notificationReducer/listSlice';
import { customersSelectors } from 'store/reducers/notificationReducer/customersSlice';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { NotificationFormFields } from '../types/notificationFormField';

/**
 * お知らせ登録 store の実装をコンポーネントから隠すための中間層
 * @returns お知らせ登録 store の selector のラッパーと action dispatch 処理 のラッパー
 */
export const useRegistrationStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const isOpenFirstModal = useAppSelector(
    registrationSelectors.isOpenFirstModal,
  );
  const isOpenSecondModal = useAppSelector(
    registrationSelectors.isOpenSecondModal,
  );
  const title = useAppSelector(registrationSelectors.title);
  const message = useAppSelector(registrationSelectors.message);
  const isHigh = useAppSelector(registrationSelectors.isHigh);
  const startDate = useAppSelector(registrationSelectors.startDate);
  const endDate = useAppSelector(registrationSelectors.endDate);
  const condition = useAppSelector(registrationSelectors.condition);
  const conditionForDisplay = useAppSelector(
    registrationSelectors.conditionForDisplay,
  );
  const shouldShowCustomerList = useAppSelector(
    registrationSelectors.shouldShowCustomerList,
  );

  // postNotification 用
  const selectedCustomers = useAppSelector(
    customersSelectors.selectedCustomers,
  );

  const init = useCallback(() => {
    dispatch(registrationActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const goSecondModal = useCallback(() => {
    dispatch(registrationActions.stepMovedToSecond());
  }, [dispatch]);

  const goFirstModal = useCallback(() => {
    dispatch(registrationActions.stepMovedToFirst());
  }, [dispatch]);

  const setCondition = useCallback(
    (_condition: Condition) => {
      dispatch(
        registrationActions.conditionUpdated({
          condition: _condition,
        }),
      );
    },
    [dispatch],
  );

  const setForm = useCallback(
    (form: NotificationFormFields) => {
      dispatch(
        registrationActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const postNotificationAndReload = useCallback(async () => {
    dispatch(loaderActions.setLoading(true));
    const params = notificationApi.createPostNotificationParams(
      title,
      message,
      isHigh,
      startDate,
      endDate,
      condition,
      selectedCustomers,
    );
    const result = await notificationApi.postNotification(params);
    if (result.ok) {
      // お知らせ一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: 'お知らせの作成に成功しました。',
      });
    } else {
      toast({
        status: 'error',
        description: 'お知らせ作成の失敗しました。',
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [
    title,
    message,
    isHigh,
    startDate,
    endDate,
    condition,
    selectedCustomers,
    toast,
    dispatch,
  ]);

  return {
    isOpenFirstModal,
    isOpenSecondModal,
    title,
    message,
    isHigh,
    startDate,
    endDate,
    condition,
    conditionForDisplay,
    shouldShowCustomerList,
    init,
    openModal,
    goSecondModal,
    goFirstModal,
    setCondition,
    setForm,
    postNotificationAndReload,
  };
};
